/**
 * Holding a tooltip still while the pointer is not.
 *
 * A dense estate puts hundreds of marks under a few hundred pixels, so sweeping across one changes
 * what is under the pointer many times a second. Described immediately, the tooltip is a strobe:
 * it appears, names something, and is replaced before any of it can be read.
 *
 * Three rules, which are the ones a tooltip conventionally follows:
 *
 * - nothing is described until the pointer has rested on it, so crossing a mark on the way
 *   somewhere else says nothing;
 * - once something is described, moving to the next thing swaps the text at once rather than
 *   waiting again - the delay buys the reader quiet before the first tooltip, and re-paying it on
 *   every neighbour would be the strobe again with gaps in it;
 * - leaving keeps the last thing a moment longer, so passing over a gap between two marks does
 *   not blink the tooltip out and back.
 *
 * The highlight on the canvas is deliberately not on this clock. It is the answer to "what am I
 * pointing at", and an answer that arrives a sixth of a second late reads as a view that is
 * struggling rather than one that is being careful.
 */

import { useEffect, useRef, useState } from 'react';

/** How long the pointer rests before the first tooltip. `@ardoq/tooltip`'s own delay. */
export const HOVER_DWELL = 150;

/**
 * How long the last tooltip survives the pointer leaving.
 *
 * Long enough to cross the gap between two marks in a cluster, short enough that a tooltip left
 * behind on empty canvas reads as a fade rather than as something stuck.
 */
export const HOVER_GRACE = 120;

/** Fade length, in milliseconds. Shared with the style so the unmount cannot outrun the paint. */
export const HOVER_FADE = 120;

export type SettledHover<T> = {
  /** What to describe, kept through the fade so the tooltip does not vanish mid-transition. */
  target: T | null;
  /** What the opacity should be now. */
  isShown: boolean;
};

/**
 * The hovered thing, delayed on the way in and on the way out.
 *
 * Takes whatever the caller hovers and hands back what should be described. `null` in means the
 * pointer is over nothing; `null` out means there is nothing left to draw, which is not the same
 * moment.
 */
export type SettledHoverArgs<T> = {
  /** What is hovered now, anchor and all. */
  hovered: T | null;
  /**
   * What is hovered, ignoring where on it the pointer is. The clock runs on this.
   *
   * Keyed on the whole hover, the wait restarted on every pixel of movement - a caller rebuilds it
   * per `pointermove` because the anchor moves - so the delay only began once the pointer was dead
   * still, rather than when it arrived. Crossing a region slowly, which is how a reader reads a
   * large one, said nothing at all. `@ardoq/tooltip` starts its delay on enter, and so does this.
   */
  identity: unknown;
  /**
   * What the description is about. Replacing it drops the tooltip at once rather than fading it.
   *
   * A fade is for the pointer leaving - the thing being described is still there, and easing it
   * away is a courtesy. Data being replaced is not that: the subject is gone, and a tooltip that
   * kept naming it for another fifth of a second would be describing a component the view no
   * longer holds.
   */
  subject: unknown;
};

export const useSettledHover = <T>({
  hovered,
  identity,
  subject,
}: SettledHoverArgs<T>): SettledHover<T> => {
  const [settled, setSettled] = useState<SettledHover<T>>({
    target: null,
    isShown: false,
  });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /* Reset during render rather than in an effect, so no frame is painted with the old subject's
     text over the new one's picture. */
  const [described, setDescribed] = useState(subject);
  if (described !== subject) {
    setDescribed(subject);
    clearTimeout(timer.current);
    setSettled({ target: null, isShown: false });
  }

  /* Read inside the timeouts rather than captured, so a target that changes while one is pending
     is the one that gets shown. */
  const latest = useRef(hovered);
  latest.current = hovered;
  const shown = useRef(settled.isShown);
  shown.current = settled.isShown;
  const held = useRef(settled.target);
  held.current = settled.target;

  useEffect(() => {
    clearTimeout(timer.current);

    /**
     * Anything invisible is let go of here, whatever happens next.
     *
     * A target outlives `isShown` only so there is something to fade. The moment that fade is
     * cancelled - by the pointer arriving somewhere new, or by it leaving again - carrying the
     * target further would leave the canvas dimmed around something the pointer has gone from,
     * with no tooltip to name it. Both of those were separate bugs, and this is the one place
     * every path below passes through, so a path added later cannot strand one either.
     */
    if (!shown.current && held.current !== null) {
      setSettled({ target: null, isShown: false });
    }

    /* Read rather than depended on. The clock must not re-run when only the anchor moved, which
       is what listing `hovered` here would make it do. */
    if (latest.current !== null) {
      /* Already describing something: swap now. The reader has paid the delay once. */
      if (shown.current) {
        setSettled({ target: latest.current, isShown: true });
        return;
      }
      timer.current = setTimeout(() => {
        const target = latest.current;
        if (target !== null) setSettled({ target, isShown: true });
      }, HOVER_DWELL);
      return;
    }

    if (!shown.current) return;

    timer.current = setTimeout(() => {
      setSettled(current => ({ ...current, isShown: false }));
      /* Kept mounted for the fade, then dropped - otherwise the tooltip disappears on the frame
         the transition starts and there is nothing to fade. */
      timer.current = setTimeout(
        () => setSettled({ target: null, isShown: false }),
        HOVER_FADE
      );
    }, HOVER_GRACE);
  }, [identity]);

  /**
   * The anchor follows the pointer, which the clock above deliberately no longer sees.
   *
   * Moving across the same mark is not a new hover, so it must not restart the wait - but the
   * tooltip still has to sit by the cursor rather than where the cursor first was. Only while
   * something is shown: before that there is nothing on screen to follow.
   */
  useEffect(() => {
    if (!shown.current || hovered === null) return;
    setSettled(current =>
      current.target === hovered ? current : { target: hovered, isShown: true }
    );
  }, [hovered]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return settled;
};
